import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaQuoteLeft, FaQuoteRight, FaStar, FaStarHalfAlt, FaUser, FaUserTie, FaUserGraduate, FaUserMd, FaUserNurse } from 'react-icons/fa';

// Section title underline animation
const underlineAnimation = keyframes`
  0% { width: 0; }
  100% { width: 120px; }
`;

// Testimonial slide in animation
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const TestimonialsContainer = styled.section`
  background: linear-gradient(135deg, #0c1f25 0%, #143b43 100%);
  padding: 5rem 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(0, 225, 199, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 3rem;
  margin-bottom: 4rem;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 3rem;
  }
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 4px;
    background: linear-gradient(to right, #fff, #00e1c7);
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
    animation: ${underlineAnimation} 1.5s ease-out forwards;
  }
`;

const CarouselContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
`;

const TestimonialSlide = styled.div`
  opacity: ${props => (props.$active ? 1 : 0)};
  position: ${props => (props.$active ? 'relative' : 'absolute')};
  top: 0;
  left: 0;
  width: 100%;
  transition: opacity 0.5s ease-in-out;
  animation: ${props => (props.$active ? slideIn : 'none')} 0.8s ease-out;
`;

const TestimonialCard = styled.div`
  background: rgba(13, 38, 45, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 20px;
  padding: 2.5rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
  box-shadow: 0 8px 32px rgba(0, 225, 199, 0.15);
  border: 1px solid rgba(0, 225, 199, 0.1);
  text-align: center;
  position: relative;
  
  &:hover {
    box-shadow: 0 12px 40px rgba(0, 225, 199, 0.25);
    border: 1px solid rgba(0, 225, 199, 0.2);
  }
`;

const IconContainer = styled.div`
  width: 70px;
  
  @media (max-width: 768px) {
    width: 60px;
  height: 70px;
  
  @media (max-width: 768px) {
    height: 60px;
  }
  border-radius: 50%;
  background: linear-gradient(145deg, #143b43, #0c1f25);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 225, 199, 0.2);
  border: 2px solid rgba(0, 225, 199, 0.2);
  
  svg {
    color: #00e1c7;
    font-size: 2rem;
    
    @media (max-width: 768px) {
      font-size: 1.7rem;
    }
  }
`;

const QuoteLeft = styled(FaQuoteLeft)`
  position: absolute;
  top: 30px;
  left: 30px;
  color: rgba(0, 225, 199, 0.2);
  font-size: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    top: 20px;
    left: 20px;
  }
`;

const QuoteRight = styled(FaQuoteRight)`
  position: absolute;
  bottom: 30px;
  right: 30px;
  color: rgba(0, 225, 199, 0.2);
  font-size: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    bottom: 20px;
    right: 20px;
  }
`;

const TestimonialText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

const UserName = styled.h4`
  color: #00e1c7;
  font-size: 1.2rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  margin-bottom: 0.5rem;
`;

const UserInfo = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const StarRating = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-top: 1rem;
  
  svg {
    color: #FFD700;
    font-size: 1.2rem;
  }
`;

const IndicatorsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 2rem;
`;

const Indicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => (props.$active ? '#00e1c7' : 'rgba(255, 255, 255, 0.3)')};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => (props.$active ? '#00e1c7' : 'rgba(255, 255, 255, 0.5)')};
  }
`;

// Testimonial data with Indian names and different reviews
const testimonials = [
  {
    id: 1,
    name: "Aditya Sharma",
    location: "Delhi",
    occupation: "Software Engineer",
    rating: 5,
    text: "ParkGanga has completely transformed my daily commute. I used to spend at least 20 minutes searching for parking near my office, but now I can book a spot in advance and drive straight to it. The real-time availability feature is incredibly accurate!",
    icon: <FaUserTie />
  },
  {
    id: 2,
    name: "Nisha Patel",
    location: "Mumbai",
    occupation: "Marketing Executive",
    rating: 4.5,
    text: "As someone who frequently visits shopping malls during weekends, finding parking was always a nightmare. ParkGanga has made my shopping experiences stress-free. The app is intuitive, and the booking process takes just seconds. Highly recommended!",
    icon: <FaUserGraduate />
  },
  {
    id: 3,
    name: "Rajiv Malhotra",
    location: "Bangalore",
    occupation: "Business Owner",
    rating: 5,
    text: "I run multiple businesses across the city and am constantly on the move. ParkGanga has saved me countless hours and reduced my stress levels significantly. The CCTV surveillance feature gives me peace of mind knowing my car is safe.",
    icon: <FaUserMd />
  },
  {
    id: 4,
    name: "Sunita Reddy",
    location: "Hyderabad",
    occupation: "College Professor",
    rating: 4.5,
    text: "Finding parking near my university was always challenging, especially during peak hours. With ParkGanga, I can reserve my spot before leaving home. The app has never failed me, and the customer service is exceptional whenever I've had questions.",
    icon: <FaUserNurse />
  },
  {
    id: 5,
    name: "Vikram Singh",
    location: "Chandigarh",
    occupation: "IT Consultant",
    rating: 5,
    text: "I travel between client locations frequently, and ParkGanga has become an essential tool in my daily routine. The app's interface is clean, loading times are fast, and the parking spot availability is always accurate. It's worth every penny!",
    icon: <FaUser />
  }
];

const TestimonialsCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef(null);
  
  // Function to render star ratings
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`star-${i}`} />);
    }
    
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half-star" />);
    }
    
    return stars;
  };
  
  // Auto-scroll functionality
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 7000); // Change testimonial every 7 seconds
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  // Reset interval when manually changing slides
  const handleIndicatorClick = (index) => {
    setActiveIndex(index);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 7000);
  };
  
  return (
    <TestimonialsContainer id="testimonials">
      <SectionTitle>What Our Users Say</SectionTitle>
      
      <CarouselContainer>
        {testimonials.map((testimonial, index) => (
          <TestimonialSlide key={testimonial.id} $active={index === activeIndex}>
            <TestimonialCard>
              <QuoteLeft />
              <IconContainer>
                {testimonial.icon}
              </IconContainer>
              <TestimonialText>"{testimonial.text}"</TestimonialText>
              <UserName>{testimonial.name}</UserName>
              <UserInfo>{testimonial.occupation} from {testimonial.location}</UserInfo>
              <StarRating>
                {renderStars(testimonial.rating)}
              </StarRating>
              <QuoteRight />
            </TestimonialCard>
          </TestimonialSlide>
        ))}
        
        <IndicatorsContainer>
          {testimonials.map((_, index) => (
            <Indicator 
              key={index} 
              $active={index === activeIndex} 
              onClick={() => handleIndicatorClick(index)}
            />
          ))}
        </IndicatorsContainer>
      </CarouselContainer>
    </TestimonialsContainer>
  );
};

export default TestimonialsCarousel;